import { ArrowUturnLeftIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Money } from '../../components/ui/Money';
import { PageHeader } from '../../components/ui/PageHeader';
import { RowAction, RowActions } from '../../components/ui/RowActions';
import { Skeleton, SkeletonRegion } from '../../components/ui/Skeleton';
import { StateBlock } from '../../components/ui/StateBlock';
import { PaymentChip, StatusChip } from '../../components/ui/StatusChip';
import { ADVANCE_LABEL_KEY } from '../../components/ui/status';
import { usePageTitle } from '../../hooks/usePageTitle';
import type { OrderStatus } from '../../types/Order';
import { isTableNumber, type PaymentChange } from '../../features/orders/orderApi';
import { PREVIOUS_STATUS, useOrderActions } from '../../features/orders/useOrderActions';
import { useOrderEditor } from '../../features/orders/useOrderEditor';
import { OrderTimeline } from '../../features/order-detail/OrderTimeline';
import { TimeInState } from '../../features/order-detail/TimeInState';
import { buildTimeline } from '../../features/order-detail/timeline';
import { useOrderDetail } from '../../features/order-detail/useOrderDetail';

dayjs.extend(relativeTime);

const Section = ({ title, className, children }: { title: string; className: string; children: ReactNode }) => (
    <Card as="section" className={className}>
        <h2 className="mb-3 text-title text-strong">{title}</h2>
        {children}
    </Card>
);

const OrderDetail = () => {
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation(['orders', 'common', 'orderDetail']);
    usePageTitle(t('title'));
    const { order, state, receivedAt, patch, reload, retry } = useOrderDetail(id);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [cancelling, setCancelling] = useState(false);

    const editor = useOrderEditor(order ? [order] : [], patch);
    // Every write is followed by a reload (also after a failure or an undo) so the timeline shows what the server recorded.
    const settle = (p: Promise<void>) => p.finally(() => void reload());
    const actions = useOrderActions({
        advance: (i) => settle(editor.advance(i)),
        cancel: (i) => settle(editor.cancel(i)),
        revert: (i, to: OrderStatus) => settle(editor.revert(i, to)),
        setPayment: (i, p: PaymentChange) => settle(editor.setPayment(i, p)),
    });

    if (state === 'loading') {
        return (
            <SkeletonRegion>
                <Skeleton className="mb-3 h-8 w-64" />
                <Skeleton className="mb-8 w-48" />
                <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                    <Skeleton className="h-64 w-full" />
                    <Skeleton className="h-64 w-full" />
                </div>
            </SkeletonRegion>
        );
    }
    if (state !== 'ready' || !order) {
        return (
            <Card padding="none">
                <StateBlock
                    variant={state === 'missing' ? 'empty' : 'error'}
                    icon={ClipboardDocumentListIcon}
                    title={state === 'missing' ? t('detail.notFound') : t('detail.loadFailed')}
                    body={state === 'missing' ? t('detail.notFoundBody') : t('common:ui.checkConnection')}
                    action={
                        state === 'missing' ? undefined : <Button onClick={() => retry()}>{t('common:ui.retry')}</Button>
                    }
                />
            </Card>
        );
    }

    const closed = order.status === 'completed' || order.status === 'cancelled';
    const unpaid = order.paymentStatus === 'unpaid';
    const advanceKey = ADVANCE_LABEL_KEY[order.status];
    const back = PREVIOUS_STATUS[order.status];
    const place = order.locationName ?? order.location?.name;
    const created = dayjs(order.createdAt);

    const menu: RowAction[] = [
        ...(unpaid
            ? [
                  { id: 'cash', label: t('menu.paidCash'), onSelect: () => void actions.pay(order, { status: 'paid', method: 'cash' }) },
                  { id: 'card', label: t('menu.paidCard'), onSelect: () => void actions.pay(order, { status: 'paid', method: 'card' }) },
              ]
            : [{ id: 'unpaid', label: t('menu.unpaid'), onSelect: () => void actions.pay(order, { status: 'unpaid' }) }]),
        ...(back && !closed
            ? [{ id: 'back', label: t('menu.back', { status: t(`common:status.${back}`) }), onSelect: () => void actions.back(order) }]
            : []),
        ...(order.status !== 'cancelled'
            ? [{ id: 'cancel', label: t('menu.cancel'), tone: 'danger' as const, separated: true, onSelect: () => setConfirmCancel(true) }]
            : []),
    ];

    const primary = advanceKey ? (
        <Button size="lg" variant={order.status === 'pending' ? 'primary' : 'strong'} fullWidth onClick={() => void actions.advance(order)}>
            {t(advanceKey, { ns: 'common' })}
        </Button>
    ) : (
        <Button size="lg" variant="outline" fullWidth icon={<ArrowUturnLeftIcon className="size-5" aria-hidden="true" />} onClick={() => void actions.back(order)}>
            {t('history.reopen')}
        </Button>
    );

    const where =
        order.orderType === 'togo' || !order.tableNumber
            ? t('common:orderType.togo')
            : isTableNumber(order.tableNumber)
              ? t('ticket.tableNumber', { number: order.tableNumber })
              : order.tableNumber;
    const timeline = buildTimeline(order.events);

    const confirm = async () => {
        setCancelling(true);
        await actions.cancel(order);
        setCancelling(false);
        setConfirmCancel(false);
    };

    return (
        <div>
            <PageHeader
                back={{ to: '/orders', label: t('title') }}
                title={t('detail.title', { number: order.orderNumber })}
                meta={
                    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
                        <StatusChip status={order.status} />
                        <span>
                            {[
                                where,
                                place,
                                `${created.format('D MMM, HH:mm')} (${created.fromNow()})`,
                            ]
                                .filter(Boolean)
                                .join(' · ')}
                        </span>
                    </span>
                }
                actions={
                    <>
                        <div className="hidden lg:block">{primary}</div>
                        {menu.length > 0 && <RowActions actions={menu} label={`#${order.orderNumber}`} />}
                    </>
                }
            />

            <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start lg:gap-6">
                <div className="contents lg:flex lg:flex-col lg:gap-4">
                    <Section title={t('detail.items')} className="order-2 lg:order-none">
                        <table className="hidden w-full text-small sm:table">
                            <caption className="sr-only">{t('detail.items')}</caption>
                            <thead>
                                <tr className="border-b border-hairline text-caption text-muted">
                                    <th scope="col" className="w-14 py-2 text-start font-medium">{t('detail.qty')}</th>
                                    <th scope="col" className="py-2 text-start font-medium">{t('detail.item')}</th>
                                    <th scope="col" className="py-2 text-end font-medium">{t('detail.unit')}</th>
                                    <th scope="col" className="py-2 text-end font-medium">{t('history.total')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {order.orderItems.map((i) => (
                                    <tr key={i.id} className="border-b border-hairline last:border-0">
                                        <td className="py-3 tabular-nums">{i.quantity} ×</td>
                                        <td className="py-3 text-strong">{i.name}</td>
                                        <td className="py-3 text-end"><Money amount={i.price} symbol={order.currencySymbol} position={order.currencySymbolPosition} /></td>
                                        <td className="py-3 text-end"><Money amount={Number(i.price) * i.quantity} symbol={order.currencySymbol} position={order.currencySymbolPosition} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <ul className="flex flex-col gap-2 sm:hidden">
                            {order.orderItems.map((i) => (
                                <li key={i.id} className="flex items-baseline justify-between gap-3 text-small">
                                    <span className="min-w-0 break-words text-strong">{i.quantity} × {i.name}</span>
                                    <Money amount={Number(i.price) * i.quantity} symbol={order.currencySymbol} position={order.currencySymbolPosition} />
                                </li>
                            ))}
                        </ul>
                        <p className="mt-3 flex items-baseline justify-between border-t border-hairline pt-3 text-heading text-strong">
                            <span>{t('history.total')}</span>
                            <Money amount={order.totalAmount} symbol={order.currencySymbol} position={order.currencySymbolPosition} />
                        </p>
                    </Section>

                    {order.customerNotes && (
                        <Section title={t('ticket.note')} className="order-3 lg:order-none">
                            <p className="whitespace-pre-wrap break-words text-small text-strong">{order.customerNotes}</p>
                        </Section>
                    )}
                </div>

                <div className="contents lg:flex lg:flex-col lg:gap-4">
                    <Section title={t('orderDetail:summary.title')} className="order-1 lg:order-none">
                        <TimeInState timing={order.timing} status={order.status} receivedAt={receivedAt} />
                    </Section>
                    <Section title={t('history.payment')} className="order-4 lg:order-none">
                        <div className="flex flex-wrap items-center gap-2">
                            <PaymentChip status={order.paymentStatus} method={order.paymentMethod} orderStatus={order.status} size="lg" />
                            {unpaid ? (
                                <>
                                    <Button variant="outline" onClick={() => void actions.pay(order, { status: 'paid', method: 'cash' })}>
                                        {t('common:payment.cash')}
                                    </Button>
                                    <Button variant="outline" onClick={() => void actions.pay(order, { status: 'paid', method: 'card' })}>
                                        {t('common:payment.card')}
                                    </Button>
                                </>
                            ) : (
                                <Button variant="quiet" onClick={() => void actions.pay(order, { status: 'unpaid' })}>
                                    {t('menu.unpaid')}
                                </Button>
                            )}
                        </div>
                    </Section>
                    <Section title={t('detail.details')} className="order-5 lg:order-none">
                        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-small">
                            {place && (
                                <>
                                    <dt className="text-muted">{t('detail.location')}</dt>
                                    <dd className="text-strong">{place}</dd>
                                </>
                            )}
                            <dt className="text-muted">{t('orderDetail:detail.type')}</dt>
                            <dd className="text-strong">{where}</dd>
                            <dt className="text-muted">{t('detail.placed')}</dt>
                            <dd className="text-strong">{created.format('D MMM YYYY, HH:mm')}</dd>
                            <dt className="text-muted">{t('detail.updated')}</dt>
                            <dd className="text-strong">{dayjs(order.updatedAt).fromNow()}</dd>
                        </dl>
                    </Section>
                    <Section title={t('orderDetail:timeline.title')} className="order-6 lg:order-none">
                        <OrderTimeline rows={timeline} />
                    </Section>
                </div>
            </div>

            <div className="sticky bottom-0 z-10 -mx-gutter mt-4 bg-page px-gutter py-3 shadow-hairline lg:hidden">
                {primary}
            </div>

            <ConfirmDialog
                open={confirmCancel}
                onClose={() => setConfirmCancel(false)}
                onConfirm={() => void confirm()}
                busy={cancelling}
                title={t('confirm.title', { number: order.orderNumber })}
                confirmLabel={t('confirm.cancel')}
                cancelLabel={t('confirm.keep')}
            >
                {t('confirm.body')}
            </ConfirmDialog>
        </div>
    );
};

export default OrderDetail;
